import { useState } from "react";

import { cn } from "@/lib/utils";
import type { DetailMetric, DetailMetricRow } from "@/services/navigator/agent-detail-service";

import { NvArrowDownIcon, NvArrowRightIcon, NvInfoCircleIcon } from "./detail-icons";
import { DetailSection } from "./detail-parts";

// 검증 지표 (설계서 slide 43 · Figma ver.7 03-01 · 03-00 · 07 시안): 좌측 지표 목록 + 우측 상세. 값의 `→` 앞은 도입 전(회색), 뒤는 도입 후(주황 강조).
// 헤더 등록 희망 지표는 목록 맨 위(서버가 정렬), 지표가 1개면 목록 없이 상세만 전체 폭, 측정 기준이 없으면 그 줄만 생략.
// 실데이터 대응 규칙(07): 수치 강조는 12자 이하 값만 대형 숫자 · 그보다 길면 본문 크기, `A / B / C` 복합 지표는 항목 / 도입 전 / 도입 후 / 개선 표(도입 전이 없으면 '-',
// 개선 칩은 도입 전 · 후가 모두 있고 계산이 되는 항목만), 확정 전 목표치는 제목 옆 '목표치 · 확정 전' 칩 + 하단 측정 기준 안내 박스.

const BIG_VALUE_MAX = 12; // 이 글자 수 이하일 때만 대형 숫자

// 서버가 아직 rows를 주지 않는 응답(배포 순서가 어긋난 경우)에서도 단일 지표로 그려요
const rowsOf = (m: DetailMetric) => m.rows ?? [];

// 검증 조건 원문이 없는 목표치에 붙이는 최소 안내 (원문이 있으면 그 문장을 써요)
const TARGET_FALLBACK = "상기 수치는 확정 전 목표치이며 도입 공정 · 환경에 따라 달라질 수 있어요";

function TargetChip({ className }: { className?: string }) {
  return (
    <span className={cn("inline-flex h-6 shrink-0 items-center rounded-[6px] bg-[#FEF3C7] px-[10px] text-[13px] font-bold leading-4 text-[#B45309]", className)}>목표치 · 확정 전</span>
  );
}

// 측정 기준 안내 박스: `측정 기준 · …` + 확정 전 목표치면 검증 조건(없으면 최소 안내문) — 목표치에서는 회색 한 줄 '검증 조건' 대신 이 박스 (Figma ver.7 07)
function BasisNote({ metric, condition }: { metric: DetailMetric; condition: string | null }) {
  if (!metric.description && !metric.target && !metric.period) return null;
  const note = metric.target ? (condition ? condition.split(/\n+/).join(" · ") : TARGET_FALLBACK) : null;
  return (
    <div className="flex gap-3 rounded-[10px] bg-[#F8F9FA] px-4 py-[14px]">
      <NvInfoCircleIcon size={18} className="mt-[2px] shrink-0 text-[#8E939A]" />
      <div className="flex min-w-0 flex-col gap-1">
        {metric.description && <p className="text-[14px] font-medium leading-[22px] text-nv-text-secondary">측정 기준 · {metric.description}</p>}
        {metric.period && <p className="text-[13px] leading-5 text-[#8E939A]">측정 기간 {metric.period}</p>}
        {note && <p className="text-[13px] leading-5 text-[#8E939A]">{note}</p>}
      </div>
    </div>
  );
}

const TABLE_COLUMNS = "grid-cols-[260px_150px_170px_minmax(0,1fr)]";

function MetricRowLine({ row }: { row: DetailMetricRow }) {
  const big = !!row.after && row.after.length <= BIG_VALUE_MAX;
  return (
    <div className={cn("grid min-h-[63px] items-center gap-y-2 border-b border-[#EEEFF1] px-4 py-2", TABLE_COLUMNS)}>
      <span className="break-keep pr-4 text-[15px] font-medium leading-[22px] text-nv-text-primary">{row.label}</span>
      <span className="pr-4 text-[16px] leading-[19px] text-[#6A6F77]">{row.before ?? "-"}</span>
      <span className="flex items-center gap-2 pr-4">
        {row.before && row.after && <NvArrowRightIcon size={16} className="shrink-0 text-[#A1A8B3]" />}
        <span className={cn("break-keep text-nv-brand-text", big ? "text-[22px] font-bold leading-[26px]" : "text-[15px] font-bold leading-[22px]")}>{row.after ?? "-"}</span>
      </span>
      <span>
        {row.delta && (
          <span className="inline-flex h-6 items-center gap-1 rounded-[6px] bg-[#FFF0E8] px-[10px] text-[13px] font-bold leading-4 text-nv-brand-text">
            <NvArrowDownIcon size={14} />
            {row.delta}
          </span>
        )}
      </span>
    </div>
  );
}

// 복합 지표: 항목 / 도입 전 / 도입 후 / 개선 표 (Figma ver.7 07 시안)
function MetricTable({ metric, showTarget, condition }: { metric: DetailMetric; showTarget: boolean; condition: string | null }) {
  return (
    <div className="flex min-w-0 flex-col gap-4">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <h3 className="text-[20px] font-bold leading-6 text-nv-text-primary">{metric.name}</h3>
        {showTarget && metric.target && <TargetChip />}
      </div>
      <div className="overflow-x-auto">
        <div className="min-w-[640px]">
          <div className={cn("grid h-[37.5px] items-center border-b border-nv-text-primary px-4 text-[13px] font-medium leading-4 text-[#8E939A]", TABLE_COLUMNS)}>
            <span>항목</span>
            <span>도입 전</span>
            <span>도입 후</span>
            <span>개선</span>
          </div>
          {rowsOf(metric).map((row, i) => (
            <MetricRowLine key={`${row.label}-${i}`} row={row} />
          ))}
        </div>
      </div>
      <BasisNote metric={metric} condition={condition} />
    </div>
  );
}

function MetricDetail({ metric, showTarget, condition }: { metric: DetailMetric; showTarget: boolean; condition: string | null }) {
  if (rowsOf(metric).length > 0) return <MetricTable metric={metric} showTarget={showTarget} condition={condition} />;
  const big = !!metric.after && metric.after.length <= BIG_VALUE_MAX;
  return (
    <div className="flex min-w-0 flex-col gap-[10px]">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <h3 className="text-[20px] font-medium leading-8 text-nv-text-primary">{metric.name}</h3>
        {showTarget && metric.target && <TargetChip />}
      </div>
      <div className="flex flex-wrap items-start gap-y-3">
        {metric.before && (
          <div className="mt-[13px] flex min-h-[80px] min-w-[187px] flex-col justify-center gap-[2px] rounded-nv-card bg-nv-bg-page px-5 py-3">
            <span className="text-[12px] leading-[19px] text-nv-text-tertiary">도입 전</span>
            <span className="text-[22px] leading-[35px] text-nv-text-secondary">{metric.before}</span>
          </div>
        )}
        {metric.before && metric.after && (
          <span aria-hidden className="ml-[17px] mr-2 mt-[26px] inline-block w-[38px] text-center text-[28px] leading-[45px] text-nv-text-tertiary">
            →
          </span>
        )}
        {metric.after && (
          <div className="flex min-w-0 flex-col">
            <span className="flex items-baseline gap-3">
              <span className="text-[12px] leading-[13px] text-nv-text-tertiary">도입 후</span>
              {metric.delta && <span className="text-[16px] leading-[17px] text-nv-brand-text">{metric.delta}</span>}
            </span>
            {/* 12자 이하만 대형 숫자, 더 길면 본문 크기 (Figma ver.7 실데이터 대응 규칙) */}
            <span className={cn("break-keep text-nv-brand-text", big ? "text-[40px] font-light leading-[48px] xl:text-[64px] xl:leading-[67px]" : "mt-1 text-[20px] font-medium leading-[30px] xl:text-[22px] xl:leading-8")}>{metric.after}</span>
          </div>
        )}
      </div>
      {metric.target ? (
        <div className="mt-[11px] max-w-[685px]">
          <BasisNote metric={metric} condition={condition} />
        </div>
      ) : (
        <>
          {metric.description && <p className="mt-[11px] max-w-[685px] text-[15px] leading-6 text-nv-text-secondary">{metric.description}</p>}
          {metric.period && <p className="mt-[2px] text-[13px] leading-[21px] text-nv-text-tertiary">측정 기간 {metric.period}</p>}
        </>
      )}
    </div>
  );
}

export function MetricsSection({ data }: { data: { items: DetailMetric[]; condition: string | null; target?: boolean } }) {
  const [active, setActive] = useState(0);
  const metric = data.items[Math.min(active, data.items.length - 1)];
  // 복합 지표(표)가 하나라도 있으면 표 배치(목록 340 · 상세 764), 아니면 기존 배치(363 · 749)
  const tableMode = data.items.some((m) => rowsOf(m).length > 0);
  const allTarget = !!data.target;
  return (
    <DetailSection id="metrics" title="검증 지표" titleExtra={allTarget ? <TargetChip /> : undefined}>
      <div className="flex flex-col gap-7">
        {data.items.length > 1 ? (
          <div className={cn("grid gap-6", tableMode ? "lg:grid-cols-[340px_minmax(0,1fr)] lg:gap-8" : "lg:grid-cols-[363px_minmax(0,1fr)]")}>
            <ul className={cn("flex flex-col self-start", tableMode ? "gap-[10px]" : "gap-[9px]")}>
              {data.items.map((m, i) => (
                <li key={`${m.name}-${i}`}>
                  <button
                    type="button"
                    onClick={() => setActive(i)}
                    aria-pressed={i === active}
                    className={cn(
                      "relative flex w-full text-left",
                      tableMode
                        ? cn("min-h-[76px] flex-col justify-center gap-[6px] rounded-nv-lg border px-5 py-4", i === active ? "border-nv-brand bg-nv-brand-subtle" : "border-nv-border bg-nv-surface hover:border-nv-border-strong")
                        : cn(
                            "min-h-[58px] items-center rounded-nv-control border px-[18px] py-[14px] text-[18px] leading-6",
                            i === active ? "border-[1.5px] border-nv-brand bg-nv-brand-subtle font-medium text-nv-text-primary" : "border-nv-divider bg-nv-surface text-nv-text-secondary hover:border-nv-border-strong",
                          ),
                    )}
                  >
                    {!tableMode && i === active && <span aria-hidden className="absolute inset-y-[18px] left-[10px] w-[2px] bg-nv-brand" />}
                    {tableMode ? (
                      <>
                        <span className={cn("break-keep text-[15px] leading-[22px]", i === active ? "font-bold text-nv-text-primary" : "font-medium text-nv-text-secondary")}>{m.name}</span>
                        {rowsOf(m).length > 0 && <span className="text-[12px] leading-[14px] text-[#8E939A]">{rowsOf(m).length}개 항목</span>}
                      </>
                    ) : (
                      <span className={cn(i === active && "pl-3")}>{m.name}</span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
            <MetricDetail metric={metric} showTarget={!allTarget} condition={data.condition} />
          </div>
        ) : (
          <MetricDetail metric={metric} showTarget={!allTarget} condition={data.condition} />
        )}
        {data.condition && !metric.target && <p className="text-[14px] leading-[22px] text-nv-text-secondary">검증 조건: {data.condition}</p>}
      </div>
    </DetailSection>
  );
}
